import { type LocalRecord, stableId } from '@dovefiniscono/sync';
import { addDays, startOfWeek, toISODate } from '../lib/dates';
import {
  type Challenge,
  type ChallengeMetric,
  type ChallengeProgress,
  challengePoints,
  DEFAULT_POINTS,
  dailyChallenges,
  weeklyChallenges,
} from './challenges';

// The game is derived from what happened, never stored: every device computes the same points,
// badges and challenges from the synced data.

type Session = LocalRecord<'focusSessions'>;
type Task = LocalRecord<'tasks'>;
type Checkin = LocalRecord<'taskCheckins'>;
type CalendarEvent = LocalRecord<'events'>;
type Transaction = LocalRecord<'transactions'>;

export type GameData = {
  sessions: readonly Session[];
  tasks: readonly Task[];
  checkins: readonly Checkin[];
  events: readonly CalendarEvent[];
  transactions: readonly Transaction[];
  /** Your own challenges. */
  challenges?: readonly Challenge[];
};

/** Where points come from: the three lines that play, plus challenges and badges. */
export type Source = 'focus' | 'attivita' | 'calendario' | 'sfide' | 'distintivi';
export type QuestSection = 'focus' | 'attivita' | 'calendario';

export const POINTS = {
  focusMinute: 1,
  session: 5,
  task: 10,
  taskOnTime: 5,
  checklistItem: 2,
  goalUnit: 3,
  goalMet: 20,
  blockKept: 15,
  dayRecorded: 5,
  quest: 25,
  perfectDay: 25,
  weekly: 100,
  weeklyGoal: 50,
  weeklyReview: 40,
} as const;

/** Points of each badge grade, I to IV. */
export const TIER_POINTS = [25, 50, 100, 200] as const;
export const TIER_NAMES = ['I', 'II', 'III', 'IV'] as const;

/** Points needed to reach a level: 0, 100, 300, 600, 1000… (each level asks 100 more). */
export const pointsForLevel = (level: number) => 50 * level * (level - 1);

export function levelFor(points: number) {
  let level = 1;
  while (pointsForLevel(level + 1) <= points) level++;
  const start = pointsForLevel(level);
  const next = pointsForLevel(level + 1);
  return { level, start, next, progress: (points - start) / (next - start) };
}

export const RANKS = [
  { from: 1, title: 'Apprendista' },
  { from: 5, title: 'Costante' },
  { from: 10, title: 'Concentrato' },
  { from: 15, title: 'Metodico' },
  { from: 20, title: 'Inarrestabile' },
  { from: 30, title: 'Maestro del tempo' },
] as const;

export function rankFor(level: number) {
  const index = RANKS.findLastIndex((rank) => level >= rank.from);
  return { title: RANKS[index]?.title ?? RANKS[0].title, next: RANKS[index + 1] ?? null };
}

/** What happened on one day, the ground for its points and challenges. */
export type DayStats = {
  sessions: number;
  minutes: number;
  /** Sessions started before noon. */
  morning: number;
  tasks: number;
  /** Tasks closed by their due date, and strictly before it. */
  onTime: number;
  early: number;
  /** Tasks closed after their due date. */
  late: number;
  /** High-priority tasks closed. */
  high: number;
  units: number;
  goalsMet: number;
  /** Blocks of time planned on this day, and those kept (worked on that day). */
  blocks: number;
  blocksKept: number;
  /** Blocks made on this day for the day after. */
  plannedTomorrow: number;
  /** Movements entered on the day they happened. */
  recorded: number;
};

const emptyDay = (): DayStats => ({
  sessions: 0,
  minutes: 0,
  morning: 0,
  tasks: 0,
  onTime: 0,
  early: 0,
  late: 0,
  high: 0,
  units: 0,
  goalsMet: 0,
  blocks: 0,
  blocksKept: 0,
  plannedTomorrow: 0,
  recorded: 0,
});

type QuestDef = {
  id: string;
  section: QuestSection;
  title: string;
  target: number;
  value: (day: DayStats) => number;
};

const quest = (
  section: QuestSection,
  id: string,
  title: string,
  target: number,
  value: (day: DayStats) => number,
): QuestDef => ({ id, section, title, target, value });

const QUESTS: Record<QuestSection, QuestDef[]> = {
  focus: [
    quest('focus', 'sessioni-2', 'Completa 2 sessioni di focus', 2, (d) => d.sessions),
    quest('focus', 'sessioni-4', 'Completa 4 sessioni di focus', 4, (d) => d.sessions),
    quest('focus', 'minuti-60', '60 minuti di focus', 60, (d) => d.minutes),
    quest('focus', 'minuti-90', '90 minuti di focus', 90, (d) => d.minutes),
    quest('focus', 'mattina', 'Una sessione di focus prima delle 12', 1, (d) => d.morning),
  ],
  attivita: [
    quest('attivita', 'fatte-2', 'Completa 2 attività', 2, (d) => d.tasks),
    quest('attivita', 'fatte-3', 'Completa 3 attività', 3, (d) => d.tasks),
    quest('attivita', 'anticipo', 'Chiudi un’attività prima della scadenza', 1, (d) => d.early),
    quest('attivita', 'ritardo', 'Recupera un’attività in ritardo', 1, (d) => d.late),
    quest('attivita', 'priorita', 'Completa un’attività ad alta priorità', 1, (d) => d.high),
  ],
  calendario: [
    quest('calendario', 'blocco', 'Rispetta un blocco di tempo', 1, (d) => d.blocksKept),
    quest('calendario', 'domani', 'Pianifica un blocco per domani', 1, (d) => d.plannedTomorrow),
    quest('calendario', 'spese', 'Registra le spese di oggi', 1, (d) => d.recorded),
  ],
};

type WeekStats = {
  minutes: number;
  sessions: number;
  tasks: number;
  focusDays: number;
  blocksKept: number;
  recordedDays: number;
};

const WEEKLY: { id: string; title: string; target: number; value: (week: WeekStats) => number }[] =
  [
    { id: 'ore-5', title: '5 ore di focus questa settimana', target: 300, value: (w) => w.minutes },
    {
      id: 'fatte-15',
      title: 'Completa 15 attività questa settimana',
      target: 15,
      value: (w) => w.tasks,
    },
    { id: 'giorni-5', title: 'Focus in 5 giorni su 7', target: 5, value: (w) => w.focusDays },
    {
      id: 'blocchi-5',
      title: 'Rispetta 5 blocchi di tempo',
      target: 5,
      value: (w) => w.blocksKept,
    },
  ];

export type Quest = {
  id: string;
  section: QuestSection | 'settimana';
  title: string;
  target: number;
  /** Progress, never past the target. */
  done: number;
  met: boolean;
  points: number;
  /** One of your own challenges. */
  mine?: { challengeId: string; metric: ChallengeMetric; tickedToday: boolean };
};

/** A stable number from a text: the same day always draws the same challenges. */
function hash(text: string): number {
  let value = 2166136261;
  for (let index = 0; index < text.length; index++) {
    value ^= text.charCodeAt(index);
    value = Math.imul(value, 16777619);
  }
  return value >>> 0;
}

const toQuest = (
  def: { id: string; title: string; target: number },
  section: Quest['section'],
  value: number,
  points: number,
): Quest => ({
  id: def.id,
  section,
  title: def.title,
  target: def.target,
  done: Math.min(value, def.target),
  met: value >= def.target,
  points,
});

/** The three challenges of a day, one per line, drawn from the date. */
export function questsFor(day: string, stats: DayStats | undefined): Quest[] {
  const today = stats ?? emptyDay();
  return (Object.keys(QUESTS) as QuestSection[]).map((section) => {
    const pool = QUESTS[section];
    const def = pool[hash(`${day}:${section}`) % pool.length] as QuestDef;
    return toQuest(def, section, def.value(today), POINTS.quest);
  });
}

function weekStats(days: ReadonlyMap<string, DayStats>, monday: string): WeekStats {
  const week: WeekStats = {
    minutes: 0,
    sessions: 0,
    tasks: 0,
    focusDays: 0,
    blocksKept: 0,
    recordedDays: 0,
  };
  for (let index = 0; index < 7; index++) {
    const day = days.get(addDays(monday, index));
    if (!day) continue;
    week.minutes += day.minutes;
    week.tasks += day.tasks;
    week.blocksKept += day.blocksKept;
    week.sessions += day.sessions;
    if (day.sessions > 0) week.focusDays++;
    if (day.recorded > 0) week.recordedDays++;
  }
  return week;
}

/** The challenge of the week that starts on `monday`. */
export function weeklyFor(days: ReadonlyMap<string, DayStats>, monday: string): Quest {
  const def = WEEKLY[hash(`settimana:${monday}`) % WEEKLY.length] as (typeof WEEKLY)[number];
  return toQuest(def, 'settimana', def.value(weekStats(days, monday)), POINTS.weekly);
}

/** A number you set yourself for every week. */
export type WeeklyGoal = { id: string; metric: WeeklyMetric; target: number; since: string };
export type WeeklyMetric = 'focusMinutes' | 'sessions' | 'tasks' | 'blocksKept' | 'recordedDays';

/** Your choices that change the game: days off and your own weekly goals. */
export type GamePreferences = {
  /** 0 = Monday … 6 = Sunday. */
  restWeekdays: readonly number[];
  restDays: readonly string[];
  weeklyGoals: readonly WeeklyGoal[];
  /** Mondays of the weeks reviewed. */
  reviewedWeeks?: readonly string[];
};

export const NO_PREFERENCES: GamePreferences = { restWeekdays: [], restDays: [], weeklyGoals: [] };

const METRIC_VALUE: Record<WeeklyMetric, (week: WeekStats) => number> = {
  focusMinutes: (w) => w.minutes,
  sessions: (w) => w.sessions,
  tasks: (w) => w.tasks,
  blocksKept: (w) => w.blocksKept,
  recordedDays: (w) => w.recordedDays,
};

/** "5 ore di focus", "20 attività completate": a weekly goal in words. */
export function weeklyGoalTitle(goal: Pick<WeeklyGoal, 'metric' | 'target'>): string {
  const { metric, target } = goal;
  switch (metric) {
    case 'focusMinutes':
      return target % 60 === 0
        ? `${target / 60} ${target === 60 ? 'ora' : 'ore'} di focus`
        : `${target} minuti di focus`;
    case 'sessions':
      return `${target} ${target === 1 ? 'sessione' : 'sessioni'} di focus`;
    case 'tasks':
      return `${target} ${target === 1 ? 'attività completata' : 'attività completate'}`;
    case 'blocksKept':
      return `${target} ${target === 1 ? 'blocco di tempo rispettato' : 'blocchi di tempo rispettati'}`;
    case 'recordedDays':
      return `Spese registrate in ${target} ${target === 1 ? 'giorno' : 'giorni'}`;
  }
}

/** The id a weekly goal of before takes as a challenge of yours. */
export const challengeIdOfGoal = (goalId: string) => stableId(`weekly-goal:${goalId}`);

/**
 * Your challenges, with the weekly goals set before challenges existed read as weekly challenges
 * (until they are turned into records: then the record wins, never both).
 */
export function allChallenges(
  challenges: readonly Challenge[],
  weeklyGoals: readonly WeeklyGoal[],
): Challenge[] {
  const ids = new Set(challenges.map((challenge) => challenge.id));
  const legacy = weeklyGoals
    .filter((goal) => !ids.has(challengeIdOfGoal(goal.id)))
    .map(
      (goal): Challenge => ({
        id: challengeIdOfGoal(goal.id),
        version: 0,
        createdAt: `${goal.since}T00:00:00.000Z`,
        updatedAt: `${goal.since}T00:00:00.000Z`,
        deletedAt: null,
        title: weeklyGoalTitle(goal),
        period: 'week',
        metric: goal.metric,
        target: goal.target,
        points: DEFAULT_POINTS.week,
        weekdays: null,
        doneDays: [],
        since: goal.since,
        archivedAt: null,
      }),
    );
  return [...challenges, ...legacy];
}

const METRIC_SECTION: Record<ChallengeMetric, QuestSection> = {
  manual: 'calendario',
  focusMinutes: 'focus',
  sessions: 'focus',
  tasks: 'attivita',
  onTime: 'attivita',
  units: 'attivita',
  blocksKept: 'calendario',
  recordedDays: 'calendario',
};

/** A challenge of yours shown like the others. */
export const questOfChallenge = (progress: ChallengeProgress): Quest => ({
  id: progress.id,
  section: progress.period === 'week' ? 'settimana' : METRIC_SECTION[progress.metric],
  title: progress.title,
  target: progress.target,
  done: progress.done,
  met: progress.met,
  points: progress.points,
  mine: {
    challengeId: progress.challengeId,
    metric: progress.metric,
    tickedToday: progress.tickedToday,
  },
});

/** Your goals for the week that starts on `monday`. */
export function weeklyGoalsFor(
  days: ReadonlyMap<string, DayStats>,
  monday: string,
  goals: readonly WeeklyGoal[],
): Quest[] {
  const week = weekStats(days, monday);
  return goals
    .filter((goal) => goal.since <= monday)
    .map((goal) =>
      toQuest(
        { id: goal.id, title: weeklyGoalTitle(goal), target: goal.target },
        'settimana',
        METRIC_VALUE[goal.metric](week),
        POINTS.weeklyGoal,
      ),
    );
}

/** Monday-based weekday of a day: 0 = Monday … 6 = Sunday. */
const weekdayOf = (day: string) => (new Date(`${day}T12:00:00`).getDay() + 6) % 7;

type BadgeStats = {
  sessions: number;
  hours: number;
  bestDayMinutes: number;
  bestStreak: number;
  earlyBird: number;
  bestRun: number;
  tasks: number;
  onTime: number;
  goalsMet: number;
  checklist: number;
  bestGoalStreak: number;
  blocksKept: number;
  weeksPlanned: number;
  recordedDays: number;
  perfectDays: number;
  weeklyWon: number;
};

type BadgeDef = {
  id: string;
  section: Exclude<Source, 'distintivi'>;
  title: string;
  /** What is counted, in words: "37 sessioni su 100". */
  unit: string;
  tiers: readonly [number, number, number, number];
  value: (stats: BadgeStats) => number;
};

const BADGES: BadgeDef[] = [
  {
    id: 'sessioni',
    section: 'focus',
    title: 'Pomodori',
    unit: 'sessioni completate',
    tiers: [1, 25, 100, 500],
    value: (s) => s.sessions,
  },
  {
    id: 'ore',
    section: 'focus',
    title: 'Ore di focus',
    unit: 'ore',
    tiers: [1, 10, 50, 200],
    value: (s) => s.hours,
  },
  {
    id: 'giornata',
    section: 'focus',
    title: 'Giornata piena',
    unit: 'minuti in un giorno',
    tiers: [60, 120, 240, 360],
    value: (s) => s.bestDayMinutes,
  },
  {
    id: 'costanza',
    section: 'focus',
    title: 'Costanza',
    unit: 'giorni di fila',
    tiers: [3, 7, 30, 100],
    value: (s) => s.bestStreak,
  },
  {
    id: 'mattiniero',
    section: 'focus',
    title: 'Mattiniero',
    unit: 'sessioni prima delle 9',
    tiers: [1, 10, 30, 100],
    value: (s) => s.earlyBird,
  },
  {
    id: 'filotto',
    section: 'focus',
    title: 'Filotto',
    unit: 'sessioni di fila senza interruzioni',
    tiers: [4, 8, 16, 32],
    value: (s) => s.bestRun,
  },
  {
    id: 'fatte',
    section: 'attivita',
    title: 'Cose fatte',
    unit: 'attività completate',
    tiers: [10, 50, 250, 1000],
    value: (s) => s.tasks,
  },
  {
    id: 'puntuale',
    section: 'attivita',
    title: 'Puntuale',
    unit: 'attività chiuse entro la scadenza',
    tiers: [5, 25, 100, 300],
    value: (s) => s.onTime,
  },
  {
    id: 'traguardi',
    section: 'attivita',
    title: 'Traguardi',
    unit: 'traguardi raggiunti',
    tiers: [1, 10, 50, 200],
    value: (s) => s.goalsMet,
  },
  {
    id: 'spunte',
    section: 'attivita',
    title: 'Spunte',
    unit: 'elementi di checklist fatti',
    tiers: [10, 50, 200, 1000],
    value: (s) => s.checklist,
  },
  {
    id: 'abitudine',
    section: 'attivita',
    title: 'Abitudine',
    unit: 'giorni di fila con il traguardo',
    tiers: [3, 7, 21, 60],
    value: (s) => s.bestGoalStreak,
  },
  {
    id: 'blocchi',
    section: 'calendario',
    title: 'Di parola',
    unit: 'blocchi di tempo rispettati',
    tiers: [1, 10, 50, 200],
    value: (s) => s.blocksKept,
  },
  {
    id: 'settimane',
    section: 'calendario',
    title: 'Settimana in mano',
    unit: 'settimane con blocchi in 3 giorni',
    tiers: [1, 4, 12, 52],
    value: (s) => s.weeksPlanned,
  },
  {
    id: 'registro',
    section: 'calendario',
    title: 'Conti in ordine',
    unit: 'giorni con le spese registrate in giornata',
    tiers: [7, 30, 100, 365],
    value: (s) => s.recordedDays,
  },
  {
    id: 'perfette',
    section: 'sfide',
    title: 'Giornate perfette',
    unit: 'giorni con le tre sfide',
    tiers: [1, 7, 30, 100],
    value: (s) => s.perfectDays,
  },
  {
    id: 'settimanali',
    section: 'sfide',
    title: 'Sfide della settimana',
    unit: 'sfide settimanali vinte',
    tiers: [1, 4, 12, 52],
    value: (s) => s.weeklyWon,
  },
];

export type Badge = {
  id: string;
  section: Exclude<Source, 'distintivi'>;
  title: string;
  unit: string;
  /** 0 = not yet, 1–4 = grade I–IV. */
  tier: number;
  value: number;
  /** The count the next grade asks for, null at grade IV. */
  next: number | null;
  /** 0…1 towards the next grade. */
  ratio: number;
};

function badgesFrom(stats: BadgeStats): Badge[] {
  return BADGES.map((def) => {
    const value = def.value(stats);
    const tier = def.tiers.filter((threshold) => value >= threshold).length;
    const next = def.tiers[tier] ?? null;
    const previous = tier > 0 ? (def.tiers[tier - 1] ?? 0) : 0;
    return {
      id: def.id,
      section: def.section,
      title: def.title,
      unit: def.unit,
      tier,
      value,
      next,
      ratio: next === null ? 1 : Math.max(0, Math.min(1, (value - previous) / (next - previous))),
    };
  });
}

export type PointsBySource = Record<Source, number>;

const dayPoints = (day: DayStats) => ({
  focus: day.minutes * POINTS.focusMinute + day.sessions * POINTS.session,
  attivita:
    day.tasks * POINTS.task +
    day.onTime * POINTS.taskOnTime +
    day.units * POINTS.goalUnit +
    day.goalsMet * POINTS.goalMet,
  calendario: day.blocksKept * POINTS.blockKept + (day.recorded > 0 ? POINTS.dayRecorded : 0),
});

export type Game = {
  points: number;
  bySource: PointsBySource;
  level: number;
  start: number;
  next: number;
  /** 0…1 towards the next level. */
  progress: number;
  rank: string;
  nextRank: { from: number; title: string } | null;
  /** Points earned today, by line (checklist ticks and badges have no day). */
  today: PointsBySource;
  quests: Quest[];
  weekly: Quest;
  /** Your own challenges of today. */
  myDaily: Quest[];
  /** Your own challenges of this week. */
  myWeekly: Quest[];
  perfectToday: boolean;
  /** Today is a day off: the streak waits. */
  restToday: boolean;
  /** Weekly reviews done. */
  reviews: number;
  isRest: (day: string) => boolean;
  badges: Badge[];
  streak: number;
  bestStreak: number;
  perfectDays: ReadonlySet<string>;
  /** Blocks of time that were kept. */
  keptBlocks: ReadonlySet<string>;
  days: ReadonlyMap<string, DayStats>;
};

const dayOf = (instant: string) => toISODate(new Date(instant));

/** Ever-growing days are bounded: three years of history is plenty for challenges. */
const MAX_DAYS = 1100;

export function computeGame(
  data: GameData,
  today: string,
  preferences: GamePreferences = NO_PREFERENCES,
): Game {
  const restDays = new Set(preferences.restDays);
  const restWeekdays = new Set(preferences.restWeekdays);
  const isRest = (day: string) => restDays.has(day) || restWeekdays.has(weekdayOf(day));
  const days = new Map<string, DayStats>();
  const at = (day: string) => {
    let stats = days.get(day);
    if (!stats) {
      stats = emptyDay();
      days.set(day, stats);
    }
    return stats;
  };

  // Focus: completed sessions, and runs without an interrupted one.
  let sessions = 0;
  let minutes = 0;
  let earlyBird = 0;
  let run = 0;
  let bestRun = 0;
  const workedOn = new Set<string>();
  const finished = data.sessions
    .filter((session) => session.endedAt !== null)
    .sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  for (const session of finished) {
    if (!session.completed) {
      run = 0;
      continue;
    }
    const day = dayOf(session.startedAt);
    const hour = new Date(session.startedAt).getHours();
    const stats = at(day);
    stats.sessions++;
    stats.minutes += session.plannedMinutes;
    if (hour < 12) stats.morning++;
    if (hour < 9) earlyBird++;
    sessions++;
    minutes += session.plannedMinutes;
    run++;
    bestRun = Math.max(bestRun, run);
    if (session.taskId) workedOn.add(`${session.taskId}|${day}`);
  }

  // Tasks: closed, on time, and checklist ticks (which have no date).
  let tasks = 0;
  let onTime = 0;
  let checklist = 0;
  for (const task of data.tasks) {
    checklist += (task.checklist ?? []).filter((item) => item.done).length;
    if (!task.completedAt) continue;
    const day = dayOf(task.completedAt);
    const stats = at(day);
    stats.tasks++;
    tasks++;
    if (task.priority === 3) stats.high++;
    if (task.dueDate) {
      if (day <= task.dueDate) {
        stats.onTime++;
        onTime++;
      }
      if (day < task.dueDate) stats.early++;
      if (day > task.dueDate) stats.late++;
    }
    workedOn.add(`${task.id}|${day}`);
  }

  // Goal units: a focus session ticks once, even if two devices both saw it end.
  const unitsByTask = new Map<string, Map<string, number>>();
  const seenSessions = new Set<string>();
  for (const checkin of data.checkins) {
    if (checkin.sessionId) {
      if (seenSessions.has(checkin.sessionId)) continue;
      seenSessions.add(checkin.sessionId);
    }
    const day = dayOf(checkin.at);
    at(day).units += checkin.units;
    const byDay = unitsByTask.get(checkin.taskId) ?? new Map<string, number>();
    byDay.set(day, (byDay.get(day) ?? 0) + checkin.units);
    unitsByTask.set(checkin.taskId, byDay);
    workedOn.add(`${checkin.taskId}|${day}`);
  }

  // Goals met: each day, week or the whole, counted on the day the target was crossed.
  let goalsMet = 0;
  let bestGoalStreak = 0;
  for (const task of data.tasks) {
    const goal = task.goal;
    const byDay = unitsByTask.get(task.id);
    if (!goal || !byDay) continue;
    const sorted = [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b));
    const crossed = (day: string) => {
      at(day).goalsMet++;
      goalsMet++;
    };
    if (goal.period === 'day') {
      let streak = 0;
      let previous: string | null = null;
      for (const [day, units] of sorted) {
        if (units < goal.units) continue;
        crossed(day);
        streak = previous !== null && addDays(previous, 1) === day ? streak + 1 : 1;
        previous = day;
        bestGoalStreak = Math.max(bestGoalStreak, streak);
      }
    } else {
      let total = 0;
      let period = '';
      for (const [day, units] of sorted) {
        const current = goal.period === 'week' ? startOfWeek(day) : 'sempre';
        if (current !== period) {
          period = current;
          total = 0;
        }
        const before = total;
        total += units;
        if (before < goal.units && total >= goal.units) crossed(day);
      }
    }
  }

  // Calendar: blocks of time for tasks, kept when the task was worked on that day.
  const keptBlocks = new Set<string>();
  const blockDaysByWeek = new Map<string, Set<string>>();
  let blocksKept = 0;
  for (const event of data.events) {
    if (!event.taskId || event.source === 'google') continue;
    const day = dayOf(event.startsAt);
    const created = dayOf(event.createdAt);
    if (day === addDays(created, 1)) at(created).plannedTomorrow++;
    if (day > today) continue;
    const stats = at(day);
    stats.blocks++;
    const week = startOfWeek(day);
    const weekDays = blockDaysByWeek.get(week) ?? new Set<string>();
    weekDays.add(day);
    blockDaysByWeek.set(week, weekDays);
    if (workedOn.has(`${event.taskId}|${day}`)) {
      stats.blocksKept++;
      blocksKept++;
      keptBlocks.add(event.id);
    }
  }
  const weeksPlanned = [...blockDaysByWeek.values()].filter((set) => set.size >= 3).length;

  // Money: movements entered on the day they happened.
  for (const transaction of data.transactions) {
    if (dayOf(transaction.createdAt) === transaction.occurredOn)
      at(transaction.occurredOn).recorded++;
  }
  const recordedDays = [...days.values()].filter((day) => day.recorded > 0).length;

  // Challenges, from the first day with something in it.
  const past = [...days.keys()].filter((day) => day <= today).sort();
  const first = past[0] ?? today;
  let questsDone = 0;
  const perfectDays = new Set<string>();
  let walked = 0;
  for (let day = first; day <= today && walked < MAX_DAYS; day = addDays(day, 1), walked++) {
    const quests = questsFor(day, days.get(day));
    const met = quests.filter((quest) => quest.met).length;
    questsDone += met;
    if (met === quests.length) perfectDays.add(day);
  }
  let weeklyWon = 0;
  for (let monday = startOfWeek(first); monday <= today; monday = addDays(monday, 7)) {
    if (weeklyFor(days, monday).met) weeklyWon++;
  }
  // Your own challenges, each from the day it started.
  const challenges = allChallenges(data.challenges ?? [], preferences.weeklyGoals);
  const mine = challengePoints(challenges, days, today, startOfWeek);

  // Activity streak: a day with focus, a task closed or a goal ticked.
  const active = new Set(
    [...days.entries()]
      .filter(([, d]) => d.sessions > 0 || d.tasks > 0 || d.units > 0)
      .map(([day]) => day),
  );
  // Days off neither count nor break it.
  const firstActive = [...active].sort()[0] ?? today;
  let streak = 0;
  for (
    let day = active.has(today) ? today : addDays(today, -1), steps = 0;
    day >= firstActive && steps < MAX_DAYS;
    day = addDays(day, -1), steps++
  ) {
    if (active.has(day)) streak++;
    else if (!isRest(day)) break;
  }
  let bestStreak = 0;
  let current = 0;
  for (
    let day = firstActive, steps = 0;
    day <= today && steps < MAX_DAYS;
    day = addDays(day, 1), steps++
  ) {
    if (active.has(day)) {
      current++;
      bestStreak = Math.max(bestStreak, current);
    } else if (!isRest(day) && day < today) {
      current = 0;
    }
  }

  const badges = badgesFrom({
    sessions,
    hours: Math.floor(minutes / 60),
    bestDayMinutes: Math.max(0, ...[...days.values()].map((day) => day.minutes)),
    bestStreak,
    earlyBird,
    bestRun,
    tasks,
    onTime,
    goalsMet,
    checklist,
    bestGoalStreak,
    blocksKept,
    weeksPlanned,
    recordedDays,
    perfectDays: perfectDays.size,
    weeklyWon,
  });

  const units = [...days.values()].reduce((sum, day) => sum + day.units, 0);
  const reviews = new Set((preferences.reviewedWeeks ?? []).filter((monday) => monday <= today))
    .size;
  const bySource: PointsBySource = {
    focus: minutes * POINTS.focusMinute + sessions * POINTS.session,
    attivita:
      tasks * POINTS.task +
      onTime * POINTS.taskOnTime +
      checklist * POINTS.checklistItem +
      units * POINTS.goalUnit +
      goalsMet * POINTS.goalMet,
    calendario: blocksKept * POINTS.blockKept + recordedDays * POINTS.dayRecorded,
    sfide:
      questsDone * POINTS.quest +
      perfectDays.size * POINTS.perfectDay +
      weeklyWon * POINTS.weekly +
      mine.points +
      reviews * POINTS.weeklyReview,
    distintivi: badges.reduce(
      (sum, badge) => sum + TIER_POINTS.slice(0, badge.tier).reduce((a, b) => a + b, 0),
      0,
    ),
  };
  const points = Object.values(bySource).reduce((sum, value) => sum + value, 0);

  const quests = questsFor(today, days.get(today));
  const perfectToday = quests.every((quest) => quest.met);
  const myDaily = dailyChallenges(challenges, days, today).map(questOfChallenge);
  const todayStats = days.get(today) ?? emptyDay();
  const todayPoints = dayPoints(todayStats);
  const level = levelFor(points);
  const rank = rankFor(level.level);

  return {
    points,
    bySource,
    ...level,
    rank: rank.title,
    nextRank: rank.next,
    today: {
      ...todayPoints,
      sfide:
        quests.filter((quest) => quest.met).length * POINTS.quest +
        (perfectToday ? POINTS.perfectDay : 0) +
        myDaily.filter((quest) => quest.met).reduce((sum, quest) => sum + quest.points, 0),
      distintivi: 0,
    },
    quests,
    weekly: weeklyFor(days, startOfWeek(today)),
    myDaily,
    myWeekly: weeklyChallenges(challenges, days, startOfWeek(today), today).map(questOfChallenge),
    perfectToday,
    restToday: isRest(today),
    reviews,
    isRest,
    badges,
    streak,
    bestStreak,
    perfectDays,
    keptBlocks,
    days,
  };
}
